/**
 * PhotoLibraryPage.tsx
 *
 * 1:1 port of email_system/pages/photos/photo-library.php.
 * Grid of uploaded images with folder filtering, search, multi-select with bulk
 * move/delete, upload with drag-drop & progress bar, and detail view with
 * copyable URL + folder reassignment.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, Search, FolderOpen, Download, Trash2,
  Image as ImageIcon, X, Copy, Check, Link, ChevronDown,
  Square, RectangleHorizontal, Maximize,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { usePageContext } from '@/hooks/usePageContext';
import { apiClient } from '@/services/apiClient';
import {
  uploadMarketingPhoto,
  getPhotoFolders,
  deleteMarketingPhoto,
  isAllowedPhotoType,
  photoTypeRejectionReason,
  PHOTO_ACCEPT_ATTR,
} from '@/services/marketing.service';
import { FolderSelect } from './FolderSelect';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface Photo {
  id: number;
  accountID: number;
  masterAccountID?: number;
  /**
   * True when the image belongs to the master account and every sibling account
   * sees it. Shared photos are uploaded and managed in the corporate dashboard,
   * so this page shows them read-only: no move, no delete.
   */
  shared?: boolean;
  url: string;
  originalFilename: string;
  altText: string | null;
  folder: string | null;
  folderID?: number;
  fileSize: number;
  width: number;
  height: number;
  createDate: string;
  searchTerms: string;
  attachedTo: string[];
  uploadedBy?: string | null;
}

type ScopeFilter = 'all' | 'mine' | 'shared';


/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/* ------------------------------------------------------------------ */
/*  Scopes                                                             */
/* ------------------------------------------------------------------ */
/**
 * The campaign Photo Library under Marketing -> Content Bank, whose folders the
 * entity pickers address by name. Kept as a scope table so the endpoints and
 * limits stay together in one place.
 */
export type PhotoLibraryScope = 'marketing';

interface UploadOpts {
  altText?: string;
  folder?: string;
  onProgress?: (pct: number) => void;
}

const SCOPES = {
  marketing: {
    title: 'Photo Library',
    endpoint: '/marketing/photos',
    listKey: ['marketing', 'photos'] as const,
    foldersKey: ['marketing', 'photo-folders'] as const,
    folderScope: 'photos' as const,
    getFolders: getPhotoFolders,
    upload: (file: File, opts: UploadOpts) => uploadMarketingPhoto(file, { ...opts, saveToLibrary: true }),
    remove: deleteMarketingPhoto,
    move: (ids: number[], folder: string) =>
      apiClient.post('/marketing/photos/move', { photoIds: ids, folder }).then(() => undefined),
    // A GIF is welcome, and a WebP is accepted and converted server-side. Still not "any image": HEIC, AVIF and SVG have no
    // decoder on the API, so they are refused here rather than at send time.
    accept: PHOTO_ACCEPT_ATTR,
    isAllowedType: isAllowedPhotoType,
    // Names the actual problem — an iPhone HEIC and a stray SVG need different
    // advice, and neither is helped by being told about GIFs.
    rejectionMessage: photoTypeRejectionReason,
  },
} as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function PhotoLibraryPage({ scope = 'marketing' }: { scope?: PhotoLibraryScope } = {}) {
  const cfg = SCOPES[scope];
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const updateAIContext = usePageContext(cfg.title);

  /* -- filter / search state ---------------------------------------- */
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  /* -- selection state ---------------------------------------------- */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setMoveDropdownOpen(false);
  };

  /* -- upload modal state ------------------------------------------- */
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadAlt, setUploadAlt] = useState('');
  const [uploadFolder, setUploadFolder] = useState('general');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Uploading from the library page is an explicit "keep this" action, unlike
  // the campaign pickers where the box defaults off.

  /* -- detail modal state ------------------------------------------- */
  const [detailPhoto, setDetailPhoto] = useState<Photo | null>(null);
  const [detailFolder, setDetailFolder] = useState('general');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [detailFileSize, setDetailFileSize] = useState<number | null>(null);
  const [detailDims, setDetailDims] = useState<{ w: number; h: number } | null>(null);
  const [detailViewMode, setDetailViewMode] = useState<'square' | 'wide' | 'full'>('full');
  const [detailBgDark, setDetailBgDark] = useState(true);





  /* ================================================================ */
  /*  Queries                                                         */
  /* ================================================================ */
  const { data: photos, isLoading } = useQuery({
    queryKey: cfg.listKey,
    queryFn: async () => {
      // No all_accounts flag: the API returns this account's own photos plus the
      // ones the master account shares, which is the whole visible set.
      const res = await apiClient.get(cfg.endpoint);
      return res.data.data as Photo[];
    },
  });

  // Shares its key with SmsPhotoPicker and FtpPhotoPicker, so all three must read
  // the same shape — the service getter normalises the endpoint's `folder` field
  // to `name` and carries `scope` / `isSystem` through.
  const { data: folders } = useQuery({
    queryKey: cfg.foldersKey,
    queryFn: () => cfg.getFolders(),
  });

  // Counting off the loaded photos rather than trusting the endpoint's count
  // keeps the selector honest about what clicking a folder will actually show,
  // since the scope filter narrows the grid client-side.
  const folderRows = useMemo(
    () =>
      (folders ?? []).map((f) => ({
        ...f,
        count: (photos ?? []).filter((p) => (p.folder || 'general') === f.name).length,
      })),
    [folders, photos],
  );

  const q = search.trim().toLowerCase();
  const filteredPhotos = useMemo(() => {
    let result = photos ?? [];
    if (folder) result = result.filter((p) => p.folder === folder);
    if (scopeFilter === 'shared') result = result.filter((p) => p.shared);
    else if (scopeFilter === 'mine') result = result.filter((p) => !p.shared);
    if (q) result = result.filter((p) => {
      const haystack = `${p.originalFilename ?? ''} ${p.altText ?? ''} ${p.folder ?? ''} ${p.searchTerms ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
    return result;
  }, [photos, folder, scopeFilter, q]);

  /** Shared photos are the master account's; this page can only file its own. */
  const selectedMovableIds = useMemo(
    () =>
      Array.from(selectedIds).filter(
        (id) => !(photos ?? []).find((p) => p.id === id)?.shared,
      ),
    [selectedIds, photos],
  );

  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || '(none)'}, Folder: ${folder || 'All'}`,
      dataSummary: photos
        ? `${photos.length} photo(s) displayed, ${folders?.length ?? 0} folder(s)`
        : 'Loading...',
    });
  }, [search, folder, photos, folders, updateAIContext]);

  /* ================================================================ */
  /*  Mutations                                                       */
  /* ================================================================ */
  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('No file');
      return cfg.upload(uploadFile, {
        altText: uploadAlt,
        folder: uploadFolder,
        onProgress: (pct) => setUploadProgress(pct),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      closeUploadModal();
      toast.success('Photo uploaded');
    },
    onError: () => {
      setUploadProgress(null);
      toast.error('Upload failed');
    },
  });

  const moveMut = useMutation({
    mutationFn: async ({ ids, targetFolder }: { ids: number[]; targetFolder: string }) => {
      await cfg.move(ids, targetFolder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      clearSelection();
      toast.success('Photos moved');
    },
    onError: () => toast.error('Move failed'),
  });

  /**
   * Soft delete — the API flips active to 0, so the row and the S3 object both
   * survive. Only the account's own, unattached photos can go: the API returns
   * 422 for a master-shared image or one a record still points at, and its
   * message names what is using it, so it is surfaced rather than swallowed.
   */
  const deleteMut = useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => cfg.remove(id)));
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      return { deleted: ids.length - failures.length, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      clearSelection();
      setDetailPhoto(null);
      if (deleted > 0) toast.success(`${deleted} photo${deleted === 1 ? '' : 's'} deleted`);
      if (failures.length > 0) {
        const res = (failures[0].reason as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        toast.error(res ?? `${failures.length} photo(s) could not be deleted`);
      }
    },
    onError: () => toast.error('Delete failed'),
  });

  const moveDetailMut = useMutation({
    mutationFn: async ({ id, targetFolder }: { id: number; targetFolder: string }) => {
      await cfg.move([id], targetFolder);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cfg.listKey });
      qc.invalidateQueries({ queryKey: cfg.foldersKey });
      setDetailPhoto(null);
      toast.success('Photo moved');
    },
    onError: () => toast.error('Move failed'),
  });


  /* ================================================================ */
  /*  Handlers                                                        */
  /* ================================================================ */
  const handleFileSelect = (files: FileList | null) => {
    if (!files?.length) return;
    const file = files[0];
    if (!cfg.isAllowedType(file)) {
      toast.error(cfg.rejectionMessage(file));
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    if (!uploadOpen) setUploadOpen(true);
  };

  const closeUploadModal = () => {
    setUploadOpen(false);
    setUploadFile(null);
    setUploadPreview('');
    setUploadAlt('');
    setUploadFolder('general');
    setUploadProgress(null);
    setDragOver(false);
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
    toast.success('URL copied to clipboard');
  };

  const downloadPhoto = (photo: Photo) => {
    const a = document.createElement('a');
    a.href = photo.url;
    a.download = photo.originalFilename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onPhotoCardClick = (photo: Photo) => {
    if (selectedIds.size > 0) {
      toggleSelect(photo.id);
    } else {
      setDetailPhoto(photo);
      setDetailFolder(photo.folder ?? 'general');
      setDetailViewMode('full');
    }
  };


  // Fetch real dimensions + file size when detail modal opens
  useEffect(() => {
    if (!detailPhoto) {
      setDetailFileSize(null);
      setDetailDims(null);
      return;
    }

    // Dimensions: load the image and read naturalWidth/naturalHeight
    const img = new window.Image();
    img.onload = () => {
      setDetailDims({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = detailPhoto.url;

    // File size: use server-side proxy
    if (!detailPhoto.fileSize) {
      apiClient
        .get('/marketing/media-size', { params: { url: detailPhoto.url } })
        .then((res) => {
          const size = res.data?.data?.size ?? res.data?.size ?? 0;
          setDetailFileSize(size);
        })
        .catch(() => setDetailFileSize(0));
    } else {
      setDetailFileSize(detailPhoto.fileSize);
    }
  }, [detailPhoto]);

  /* Close move dropdown on outside click */
  useEffect(() => {
    if (!moveDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.move-dropdown-container')) {
        setMoveDropdownOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [moveDropdownOpen]);

  /* ================================================================ */
  /*  Drag-and-drop helpers for upload dropzone                       */
  /* ================================================================ */
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  /* ================================================================ */
  /*  Render                                                          */
  /* ================================================================ */
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* ============================================================ */}
      {/*  Toolbar: folder pills + search + upload button              */}
      {/* ============================================================ */}
      <div className="flex-shrink-0 px-5 pt-5 pb-4 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Folder selector + manager */}
          <FolderSelect
            scope={cfg.folderScope}
            folders={folderRows}
            activeFolder={folder}
            onSelect={(f) => { setFolder(f); clearSelection(); }}
            totalCount={photos?.length ?? 0}
          />

          {/* Shared / account scope filter */}
          <div className="flex items-center rounded-md border border-slate-200 overflow-hidden">
            {([
              ['all', 'All'],
              ['mine', 'This Account'],
              ['shared', 'Shared'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => { setScopeFilter(value); clearSelection(); }}
                className={cn(
                  'px-3 py-2 text-sm transition-colors',
                  scopeFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search + Upload */}
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search photos..."
                className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md w-48 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <Button leftIcon={<Upload className="h-4 w-4" />} onClick={() => fileRef.current?.click()}>
              Upload Photo
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept={cfg.accept}
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </div>
        </div>

        {/* ============================================================ */}
        {/*  Selection bar                                               */}
        {/* ============================================================ */}
        {hasSelection && (
          <div className="flex items-center justify-between gap-4 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold">{selectedIds.size} selected</span>

              {/* Move to folder dropdown */}
              <div className="relative move-dropdown-container">
                <button
                  onClick={(e) => { e.stopPropagation(); setMoveDropdownOpen((o) => !o); }}
                  disabled={selectedMovableIds.length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/40 rounded bg-white/15 hover:bg-white/30 disabled:opacity-50 disabled:hover:bg-white/15 transition-colors"
                  title={
                    selectedMovableIds.length === 0
                      ? 'Shared photos are filed in the corporate dashboard'
                      : 'Move to Folder'
                  }
                >
                  <FolderOpen className="h-3.5 w-3.5" /> Move to Folder <ChevronDown className="h-3 w-3" />
                </button>
                {moveDropdownOpen && (
                  <div className="absolute bottom-full left-0 mb-1 bg-white border border-slate-200 rounded-md shadow-lg min-w-[160px] max-h-[200px] overflow-y-auto z-50">
                    {folders?.map((f) => (
                      <button
                        key={f.name}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-primary/10 hover:text-primary"
                        onClick={() => {
                          moveMut.mutate({ ids: selectedMovableIds, targetFolder: f.name });
                          setMoveDropdownOpen(false);
                        }}
                      >
                        {f.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Same ownership rule as Move: a shared photo is the master
                  account's, so it is removed in the corporate dashboard. */}
              <button
                onClick={() => {
                  const n = selectedMovableIds.length;
                  if (n === 0) return;
                  if (!window.confirm(`Delete ${n} photo${n === 1 ? '' : 's'} from the library?`)) return;
                  deleteMut.mutate(selectedMovableIds);
                }}
                disabled={selectedMovableIds.length === 0 || deleteMut.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/40 rounded bg-white/15 hover:bg-white/30 disabled:opacity-50 disabled:hover:bg-white/15 transition-colors"
                title={
                  selectedMovableIds.length === 0
                    ? 'Shared photos are removed in the corporate dashboard'
                    : 'Delete'
                }
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>

              {/* Only surfaced when the selection is mixed, so the count is never a surprise. */}
              {selectedMovableIds.length > 0 && selectedMovableIds.length < selectedIds.size && (
                <span className="text-xs opacity-80">
                  {selectedIds.size - selectedMovableIds.length} shared photo
                  {selectedIds.size - selectedMovableIds.length === 1 ? '' : 's'} will be skipped
                </span>
              )}
            </div>

            <button onClick={clearSelection} className="text-lg leading-none hover:opacity-80" title="Clear selection">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/*  Photo Grid                                                  */}
      {/* ============================================================ */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
      {isLoading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-lg h-48 animate-pulse" />
          ))}
        </div>
      ) : !filteredPhotos?.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ImageIcon className="h-16 w-16 text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">{q ? 'No photos match your search' : 'No images yet'}</h3>
          {!q && <p className="text-sm text-slate-500 mt-1 mb-4">Upload images to use in your email templates.</p>}
          {!q && (
            <Button leftIcon={<Upload className="h-4 w-4" />} onClick={() => fileRef.current?.click()}>
              Upload First Photo
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              className={cn(
                'group relative rounded-lg overflow-hidden border-2 aspect-square cursor-pointer transition-colors',
                selectedIds.has(photo.id)
                  ? 'border-primary ring-2 ring-primary/30'
                  : 'border-slate-200 hover:border-slate-300',
              )}
              style={{ background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #fff 0% 50%) 50%/12px 12px' }}
              onClick={() => onPhotoCardClick(photo)}
            >
              <img
                src={photo.url}
                alt={photo.altText ?? ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />

              {/* Hover info overlay */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[10px] text-white truncate">{photo.originalFilename}</p>
                <p className="text-[9px] text-white/70">{photo.folder ?? 'general'}</p>
              </div>

              {/* Always-on badge: a shared image behaves differently (read-only here). */}
              {photo.shared && (
                <span
                  className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-slate-900/70 text-[9px] font-medium text-white"
                  title="Shared across the master account — managed in the corporate dashboard"
                >
                  Shared
                </span>
              )}

              {/* Hover actions */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); copyUrl(photo.url); }}
                  className="p-1.5 rounded-full bg-white/90 shadow-sm text-slate-600 hover:text-primary"
                  title="Copy URL"
                >
                  <Link className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadPhoto(photo); }}
                  className="p-1.5 rounded-full bg-white/90 shadow-sm text-slate-600 hover:text-primary"
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Selection checkbox */}
              <div
                className={cn(
                  'absolute top-1.5 left-1.5 transition-opacity',
                  selectedIds.has(photo.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                  hasSelection && 'opacity-100',
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSelect(photo.id);
                }}
              >
                <div className={cn(
                  'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                  selectedIds.has(photo.id)
                    ? 'bg-primary border-primary'
                    : 'bg-white/80 border-slate-400 hover:border-primary',
                )}>
                  {selectedIds.has(photo.id) && <Check className="h-3 w-3 text-white" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* ============================================================ */}
      {/*  Upload Modal                                                */}
      {/* ============================================================ */}
      <Modal
        isOpen={uploadOpen}
        onClose={closeUploadModal}
        title="Upload Image"
        size="sm"
      >
        <div className="space-y-4">
          {/* Dropzone / Preview */}
          {uploadPreview ? (
            <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
              <img src={uploadPreview} alt="Preview" className="w-full max-h-48 object-contain" />
            </div>
          ) : (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors',
                dragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-300 hover:border-primary',
              )}
              onClick={() => fileRef.current?.click()}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <Upload className="h-10 w-10 text-primary/60 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Drag &amp; drop an image here</p>
              <p className="text-xs text-slate-400 mt-1">or</p>
              <span className="inline-block mt-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded cursor-pointer">
                Browse Files
              </span>
            </div>
          )}

          {/* File info */}
          {uploadFile && (
            <p className="text-xs text-slate-500">
              {uploadFile.name} &middot; {formatBytes(uploadFile.size)}
            </p>
          )}

          {/* Alt text */}
          <FormField label="Alt Text (optional)">
            <Input
              value={uploadAlt}
              onChange={(e) => setUploadAlt(e.target.value)}
              placeholder="Describe the image..."
            />
          </FormField>

          {/* Folder select */}
          <FormField label="Folder">
            <select
              value={uploadFolder}
              onChange={(e) => setUploadFolder(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="general">General</option>
              {folders
                ?.filter((f) => f.name.toLowerCase() !== 'general')
                .map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.name}
                  </option>
                ))}
            </select>
          </FormField>

          {/* Progress bar */}
          {uploadProgress != null && (
            <div className="space-y-1">
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className={cn(
                'text-xs text-center',
                uploadProgress >= 100 ? 'text-emerald-600' : 'text-slate-500',
              )}>
                {uploadProgress >= 100 ? 'Upload complete!' : `Uploading... ${uploadProgress}%`}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={closeUploadModal}>Cancel</Button>
            <Button
              loading={uploadMut.isPending}
              disabled={!uploadFile}
              onClick={() => uploadMut.mutate()}
            >
              Upload
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================================ */}
      {/*  Detail Modal                                                */}
      {/* ============================================================ */}
      <Modal
        isOpen={!!detailPhoto}
        onClose={() => setDetailPhoto(null)}
        hideCloseButton
        size="xl"
        bodyClassName="!p-0 !overflow-hidden"
      >
        {detailPhoto && (
          <div className="relative flex h-[700px]">
            <button
              type="button"
              onClick={() => setDetailPhoto(null)}
              className="absolute top-3 right-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            {/* Left: Image preview */}
            <div className="flex-1 flex flex-col p-4 min-w-0">
              <div className="flex justify-center items-center gap-1 shrink-0 mb-3">
                {([
                  { key: 'square' as const, label: 'Square', icon: Square },
                  { key: 'wide' as const, label: 'Wide', icon: RectangleHorizontal },
                  { key: 'full' as const, label: 'Full', icon: Maximize },
                ]).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDetailViewMode(key)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                      detailViewMode === key
                        ? 'bg-primary/15 text-primary'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <button
                  type="button"
                  onClick={() => setDetailBgDark(!detailBgDark)}
                  className={cn(
                    'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                    detailBgDark ? 'bg-slate-700' : 'bg-slate-300',
                  )}
                  title={detailBgDark ? 'Switch to light background' : 'Switch to dark background'}
                >
                  <span className={cn(
                    'inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform',
                    detailBgDark ? 'translate-x-[18px]' : 'translate-x-[3px]',
                  )} />
                </button>
                <span className="text-[10px] text-slate-500 ml-0.5">{detailBgDark ? 'Dark' : 'Light'}</span>
              </div>
              <div className={cn(
                'flex-1 flex items-center justify-center overflow-hidden min-h-0 rounded-lg transition-colors',
                detailBgDark ? 'bg-slate-800' : 'bg-white',
              )}>
                {detailViewMode === 'square' && (
                  <div className="w-[500px] h-[500px] overflow-hidden rounded-lg shrink-0">
                    <img src={detailPhoto.url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {detailViewMode === 'wide' && (
                  <div className="w-[500px] h-[281px] overflow-hidden rounded-lg shrink-0">
                    <img src={detailPhoto.url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {detailViewMode === 'full' && (
                  <img src={detailPhoto.url} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
                )}
              </div>
            </div>

            {/* Right: Details panel */}
            <div className="w-[250px] border-l border-slate-200 p-4 flex flex-col gap-4 shrink-0 overflow-y-auto">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Details</h3>
                <div className="space-y-2 text-xs text-slate-600">
                  <div>
                    <span className="font-medium text-slate-700 block">Filename</span>
                    <span className="break-all">{detailPhoto.originalFilename}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700 block">Current Folder</span>
                    <span>{detailPhoto.folder ?? 'general'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700 block">Dimensions</span>
                    <span>
                      {(detailDims?.w || detailPhoto.width) > 0
                        ? `${detailDims?.w || detailPhoto.width} \u00D7 ${detailDims?.h || detailPhoto.height} px`
                        : 'Loading...'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700 block">File Size</span>
                    <span>
                      {(detailFileSize ?? detailPhoto.fileSize) > 0
                        ? formatBytes(detailFileSize ?? detailPhoto.fileSize)
                        : detailFileSize === null ? 'Loading...' : 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700 block">Added</span>
                    <span>{new Date(detailPhoto.createDate).toLocaleDateString()}</span>
                  </div>
                  {detailPhoto.uploadedBy && (
                    <div>
                      <span className="font-medium text-slate-700 block">Uploaded By</span>
                      <span>{detailPhoto.uploadedBy}</span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-slate-700 block">URL</span>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="break-all text-[10px] flex-1">{detailPhoto.url}</span>
                      <button
                        onClick={() => copyUrl(detailPhoto.url)}
                        className="p-1 text-slate-400 hover:text-primary shrink-0"
                      >
                        {copiedUrl ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                {detailPhoto.shared ? (
                  <p className="text-xs text-slate-500">
                    This image is shared across the master account. Move or remove it in the
                    corporate dashboard so the change reaches every account that uses it.
                  </p>
                ) : (
                  <>
                    <label className="text-xs font-medium text-slate-700 block mb-1.5">Move to Folder</label>
                    <select
                      value={detailFolder}
                      onChange={(e) => setDetailFolder(e.target.value)}
                      className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {folders?.map((f) => (
                        <option key={f.name} value={f.name}>{f.name}</option>
                      ))}
                      {detailPhoto.folder
                        && !folders?.some((f) => f.name === detailPhoto.folder)
                        && (
                          <option value={detailPhoto.folder}>{detailPhoto.folder}</option>
                        )}
                    </select>
                    <Button
                      size="sm"
                      className="w-full mt-2"
                      disabled={detailFolder === (detailPhoto.folder ?? 'general')}
                      loading={moveDetailMut.isPending}
                      onClick={() => moveDetailMut.mutate({ id: detailPhoto.id, targetFolder: detailFolder })}
                    >
                      Move
                    </Button>

                    {/* Blocked while a record points at the image — the API
                        refuses and names what is using it, and attachedTo is
                        already loaded, so say so before the round trip. */}
                    <Button
                      size="sm"
                      variant="danger"
                      className="w-full mt-4"
                      leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                      disabled={detailPhoto.attachedTo?.length > 0}
                      loading={deleteMut.isPending}
                      title={
                        detailPhoto.attachedTo?.length > 0
                          ? `In use by: ${detailPhoto.attachedTo.join(', ')}`
                          : 'Delete this photo'
                      }
                      onClick={() => {
                        if (!window.confirm('Delete this photo from the library?')) return;
                        deleteMut.mutate([detailPhoto.id]);
                      }}
                    >
                      Delete Photo
                    </Button>
                    {detailPhoto.attachedTo?.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1.5">
                        In use by {detailPhoto.attachedTo.join(', ')} — remove it there first.
                      </p>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        )}
      </Modal>

    </div>
  );
}
